import { Quaternion, Vector3, type Bone, type Skeleton } from 'three'
import { QuaternionOneEuroFilter } from './OneEuroFilter'
import { type Landmark } from './PoseToRig'
import { type DetectedHand, type Handedness } from './MediaPipeHandProvider'

/**
 * Drives finger bones from MediaPipe HandLandmarker world-landmarks.
 *
 * Same approach as `PoseToRig` but operates per-finger and per-hand:
 * for each driven finger bone, compute a target world-space direction from a
 * landmark pair (e.g. `index_01_l` targets the direction from index-MCP →
 * index-PIP), convert into the bone's parent's local frame, and solve the
 * local rotation that aligns the bind-pose direction with the target.
 *
 * Layered on top of `PoseToRig`: `MocapController` updates the body first
 * (which writes shoulder/upperarm/forearm/hand) so the hand bone's parent
 * world transform is current by the time we write the finger rotations.
 *
 * What's NOT solved here:
 *  - Twist along the finger axis (single-direction landmark pairs are
 *    underdetermined). Fingers stay at bind-pose twist.
 *  - The hand bone itself — `HandLandmarker` doesn't track wrist
 *    orientation in a useful way (the wrist landmark is a single point), and
 *    `PoseToRig` already drives the lower-arm direction which tilts the hand
 *    parent appropriately.
 *  - Tip / leaf bones (`*_04_leaf_*`) — leaves get no useful direction.
 */

/** MediaPipe HandLandmarker world-landmark indices. */
export const HandLandmarkIndex = {
  WRIST: 0,
  THUMB_CMC: 1, THUMB_MCP: 2, THUMB_IP: 3, THUMB_TIP: 4,
  INDEX_MCP: 5, INDEX_PIP: 6, INDEX_DIP: 7, INDEX_TIP: 8,
  MIDDLE_MCP: 9, MIDDLE_PIP: 10, MIDDLE_DIP: 11, MIDDLE_TIP: 12,
  RING_MCP: 13, RING_PIP: 14, RING_DIP: 15, RING_TIP: 16,
  PINKY_MCP: 17, PINKY_PIP: 18, PINKY_DIP: 19, PINKY_TIP: 20
} as const

interface FingerBoneSpec {
  /** Bone-name prefix used to expand to `<prefix>_<l|r>`. */
  bone_name_template: string
  from_landmark: number
  to_landmark: number
}

/**
 * Per-side finger drive specs, parameterized by `_l` / `_r` suffix at apply
 * time. Three bones per finger (the leaf bone doesn't get a target).
 *
 * Naming convention matches the rest of the Mesh2Motion humanoid rigs:
 *   `thumb_01_l`, `thumb_02_l`, `thumb_03_l`,
 *   `index_01_l`, `index_02_l`, `index_03_l`, ...
 *   (and same for `_r`).
 *
 * Order is per-finger root → tip so each child sees an updated parent
 * world transform when we walk the array.
 */
const FINGER_DRIVE_SPECS_TEMPLATE: FingerBoneSpec[] = [
  // Thumb: CMC→MCP, MCP→IP, IP→TIP
  { bone_name_template: 'thumb_01', from_landmark: HandLandmarkIndex.THUMB_CMC, to_landmark: HandLandmarkIndex.THUMB_MCP },
  { bone_name_template: 'thumb_02', from_landmark: HandLandmarkIndex.THUMB_MCP, to_landmark: HandLandmarkIndex.THUMB_IP },
  { bone_name_template: 'thumb_03', from_landmark: HandLandmarkIndex.THUMB_IP, to_landmark: HandLandmarkIndex.THUMB_TIP },

  // Index: MCP→PIP, PIP→DIP, DIP→TIP
  { bone_name_template: 'index_01', from_landmark: HandLandmarkIndex.INDEX_MCP, to_landmark: HandLandmarkIndex.INDEX_PIP },
  { bone_name_template: 'index_02', from_landmark: HandLandmarkIndex.INDEX_PIP, to_landmark: HandLandmarkIndex.INDEX_DIP },
  { bone_name_template: 'index_03', from_landmark: HandLandmarkIndex.INDEX_DIP, to_landmark: HandLandmarkIndex.INDEX_TIP },

  // Middle
  { bone_name_template: 'middle_01', from_landmark: HandLandmarkIndex.MIDDLE_MCP, to_landmark: HandLandmarkIndex.MIDDLE_PIP },
  { bone_name_template: 'middle_02', from_landmark: HandLandmarkIndex.MIDDLE_PIP, to_landmark: HandLandmarkIndex.MIDDLE_DIP },
  { bone_name_template: 'middle_03', from_landmark: HandLandmarkIndex.MIDDLE_DIP, to_landmark: HandLandmarkIndex.MIDDLE_TIP },

  // Ring
  { bone_name_template: 'ring_01', from_landmark: HandLandmarkIndex.RING_MCP, to_landmark: HandLandmarkIndex.RING_PIP },
  { bone_name_template: 'ring_02', from_landmark: HandLandmarkIndex.RING_PIP, to_landmark: HandLandmarkIndex.RING_DIP },
  { bone_name_template: 'ring_03', from_landmark: HandLandmarkIndex.RING_DIP, to_landmark: HandLandmarkIndex.RING_TIP },

  // Pinky
  { bone_name_template: 'pinky_01', from_landmark: HandLandmarkIndex.PINKY_MCP, to_landmark: HandLandmarkIndex.PINKY_PIP },
  { bone_name_template: 'pinky_02', from_landmark: HandLandmarkIndex.PINKY_PIP, to_landmark: HandLandmarkIndex.PINKY_DIP },
  { bone_name_template: 'pinky_03', from_landmark: HandLandmarkIndex.PINKY_DIP, to_landmark: HandLandmarkIndex.PINKY_TIP }
]

const DEFAULT_VISIBILITY_THRESHOLD = 0.0
// HandLandmarker landmarks don't carry visibility per landmark the way
// PoseLandmarker does; we still keep the threshold plumbing for parity in
// case a future model populates it.

interface FingerCache {
  bone: Bone
  rest_local_dir: Vector3
  rest_local_quaternion: { x: number, y: number, z: number, w: number }
  filter: QuaternionOneEuroFilter
  from_landmark: number
  to_landmark: number
}

export interface HandToRigOptions {
  filter_params?: { min_cutoff?: number, beta?: number, d_cutoff?: number }
  /** Confidence-weighted smoothing strength, identical semantics to PoseToRig. */
  smoothing_strength?: number
}

export class HandToRig {
  private readonly filter_params: HandToRigOptions['filter_params']
  private smoothing_strength: number = 0
  private bone_caches: Record<Handedness, FingerCache[]> = { Left: [], Right: [] }
  private bound: boolean = false
  /** Bone-name set the user has explicitly disabled. Same shape as PoseToRig. */
  private disabled_bones: Set<string> = new Set()

  constructor (options: HandToRigOptions = {}) {
    this.filter_params = options.filter_params
    this.smoothing_strength = Math.max(0, options.smoothing_strength ?? 0)
  }

  /**
   * Bind to the skeleton, capturing per-finger-bone bind-pose state. Bones
   * that don't exist on this skeleton are silently skipped (typical for
   * Tripo rigs without fingers, or simplified human hands).
   */
  bind (skeleton: Skeleton): void {
    const bone_by_name = new Map(skeleton.bones.map(b => [b.name, b]))
    for (const handedness of ['Left', 'Right'] as Handedness[]) {
      const suffix = handedness === 'Left' ? '_l' : '_r'
      const cache: FingerCache[] = []
      for (const spec of FINGER_DRIVE_SPECS_TEMPLATE) {
        const bone_name = spec.bone_name_template + suffix
        const bone = bone_by_name.get(bone_name)
        if (bone === undefined) continue
        const child = bone.children.find((c): c is Bone => (c as Bone).isBone === true)
        if (child === undefined) continue
        const rest_local_dir = child.position.clone()
        if (rest_local_dir.lengthSq() < 1e-12) continue
        rest_local_dir.normalize()
        cache.push({
          bone,
          rest_local_dir,
          rest_local_quaternion: {
            x: bone.quaternion.x, y: bone.quaternion.y, z: bone.quaternion.z, w: bone.quaternion.w
          },
          filter: new QuaternionOneEuroFilter(this.filter_params),
          from_landmark: spec.from_landmark,
          to_landmark: spec.to_landmark
        })
      }
      this.bone_caches[handedness] = cache
    }
    this.bound = this.bone_caches.Left.length > 0 || this.bone_caches.Right.length > 0
  }

  is_bound (): boolean { return this.bound }

  /** Total count of finger bones this rig will drive (across both hands). */
  get_drivable_finger_count (): number {
    return this.bone_caches.Left.length + this.bone_caches.Right.length
  }

  /** Bone names this rig actually found (across both hands). */
  get_drivable_bone_names (): string[] {
    return [
      ...this.bone_caches.Left.map(c => c.bone.name),
      ...this.bone_caches.Right.map(c => c.bone.name)
    ]
  }

  set_smoothing_strength (strength: number): void {
    if (!Number.isFinite(strength)) return
    this.smoothing_strength = Math.max(0, Math.min(4, strength))
  }

  set_disabled_bones (names: string[]): void {
    this.disabled_bones = new Set(names)
  }

  /**
   * Apply one frame of detected hands. Each `DetectedHand` carries a
   * handedness label and 21 world landmarks; we look up the matching
   * per-side finger cache and update each finger bone's rotation.
   *
   * Reasoning for the suffix→handedness mapping: the rig's `_l` bones are
   * the model's anatomical left, and MediaPipe's `Left`/`Right` labels are
   * also the user's anatomical left/right (the runtime corrects for the
   * selfie mirror). So `Left → _l`, `Right → _r` is correct without any
   * extra flipping.
   */
  update (hands: DetectedHand[], time: number): void {
    if (!this.bound) return
    for (const hand of hands) {
      const cache = this.bone_caches[hand.handedness]
      if (cache.length === 0) continue
      this.update_one_hand(cache, hand.landmarks, time)
    }
  }

  /** Reset every per-bone smoothing filter — call on stream restart. */
  reset_filters (): void {
    for (const handedness of ['Left', 'Right'] as Handedness[]) {
      for (const c of this.bone_caches[handedness]) c.filter.reset()
    }
  }

  /**
   * Snap every driven finger bone back to its bind-pose local rotation.
   * Used by the controller when the user disables hand mocap mid-session,
   * so fingers don't freeze at whatever curled pose was last applied.
   */
  reset_to_bind_pose (): void {
    for (const handedness of ['Left', 'Right'] as Handedness[]) {
      for (const c of this.bone_caches[handedness]) {
        const r = c.rest_local_quaternion
        c.bone.quaternion.set(r.x, r.y, r.z, r.w)
        c.bone.updateMatrixWorld(true)
        c.filter.reset()
      }
    }
  }

  private update_one_hand (cache: FingerCache[], landmarks: Landmark[], time: number): void {
    const _from_world = new Vector3()
    const _to_world = new Vector3()
    const _v_target_world = new Vector3()
    const _v_target_local = new Vector3()
    const _q_target_local = new Quaternion()
    const _q_parent_world_inv = new Quaternion()

    for (const c of cache) {
      if (this.disabled_bones.has(c.bone.name)) continue
      const from = landmarks[c.from_landmark]
      const to = landmarks[c.to_landmark]
      if (from === undefined || to === undefined) continue
      const fv = from.visibility ?? 1
      const tv = to.visibility ?? 1
      if (fv < DEFAULT_VISIBILITY_THRESHOLD || tv < DEFAULT_VISIBILITY_THRESHOLD) continue

      _from_world.set(from.x, -from.y, -from.z)
      _to_world.set(to.x, -to.y, -to.z)
      _v_target_world.copy(_to_world).sub(_from_world)
      if (_v_target_world.lengthSq() < 1e-9) continue
      _v_target_world.normalize()

      const parent = c.bone.parent
      if (parent === null) continue
      parent.updateWorldMatrix(true, false)
      parent.getWorldQuaternion(_q_parent_world_inv).invert()

      _v_target_local.copy(_v_target_world).applyQuaternion(_q_parent_world_inv).normalize()
      _q_target_local.setFromUnitVectors(c.rest_local_dir, _v_target_local)

      let confidence_weight = 1
      if (this.smoothing_strength > 0) {
        const min_vis = Math.min(fv, tv)
        confidence_weight = Math.pow(Math.max(0, Math.min(1, min_vis)), this.smoothing_strength)
      }

      const smoothed = c.filter.filter(
        [_q_target_local.x, _q_target_local.y, _q_target_local.z, _q_target_local.w],
        time,
        confidence_weight
      )
      c.bone.quaternion.set(smoothed[0], smoothed[1], smoothed[2], smoothed[3])
      c.bone.updateMatrixWorld(true)
    }
  }
}
